import get from 'lodash.get';
import PropTypes from 'prop-types';
import React, { useState } from 'react';

import { sortByCreatedDate } from '../../tools/mappers';
import Card from '../Common/Card';
import { ArrowLink } from '../Common/Links';
import SmartLink from '../Common/SmartLink';
import * as styles from './CardListing.module.scss';

export const getPostTypes = (post, useContentType, category) => {
  if (useContentType) {
    let url = `/resources/content/${post.contentType?.slug}/`;
    let title = post.contentType?.title;
    if (post.__typename === 'ContentfulPageBlogPost') {
      url = `/blog/`;
      title = 'Blog Post';
    } else if (post.__typename === 'ContentfulPageCaseStudy') {
      url = '/customer-story/all/';
      title = 'Customer Story';
    }
    return (
      <span>
        <SmartLink to={url}>{title}</SmartLink>
      </span>
    );
  }
  const getFirstCategory = (cats) => {
    if (!category || category?.toLowerCase() === 'blog') return cats.slice(0, 2);
    return [
      ...cats.filter((c) => c.categoryName === category),
      ...cats.filter((c) => c.categoryName !== category).slice(0, 1),
    ];
  };
  if (!post.categories) return null;
  const types = post.categories.length === 1 ? post.categories : getFirstCategory(post.categories);

  return types.map((type, i) => (
    <span key={i}>
      {i === 1 && ', '}
      <SmartLink to={`/blog/tag/${type.slug}/`}>{type.categoryName}</SmartLink>
    </span>
  ));
};

const CardListing = ({ posts, useContentType, category, sortByPublish, noResources }) => {
  const POSTS_PER_PAGE = 9;
  const postCount = POSTS_PER_PAGE;
  const [postAmount, setPostAmount] = useState(POSTS_PER_PAGE);
  if ((!posts || posts.length === 0) && noResources) {
    return noResources();
  }
  if (!posts || posts.length === 0) return null;
  const hasMore = postAmount < get(posts, 'length', 0);
  const sortedPosts = sortByPublish
    ? posts.sort((a, b) => new Date(b.publishDate) - new Date(a.publishDate)).slice(0, postAmount)
    : posts.sort(sortByCreatedDate).slice(0, postAmount);

  const onLoadMore = (e) => {
    e && e.preventDefault();
    setPostAmount(postAmount + postCount);
  };

  return (
    <>
      <div className={styles.posts} data-analytics-section={`${useContentType ? 'resources' : 'blog'}-post-listing`}>
        {sortedPosts.map((post) => {
          let url;
          let title = post.title;
          let ctaLabel = post.contentType?.viewLabel || post.linkLabel || 'Read more';
          let postType = getPostTypes(post, useContentType, category);
          let image = post.pageMetaData?.ogImage;
          let description = post.articleDescription?.articleDescription || post.description?.description;
          switch (post.__typename) {
            case 'ContentfulPageStandard':
              url = `/${post.slug}`;
              break;
            case 'ContentfulPageCaseStudy':
              url = `/${post.slug}`;
              title = post.heading || post.pageMetaData?.pageTitle;
              break;
            case 'ContentfulPageWalledResource':
            case 'ContentfulPageResource': {
              url = `/resources/${post.slug}`;
              break;
            }
            case 'ContentfulComponentSimpleContent': {
              url = post.linkUrl;
              postType = post.subtitle;
              image = post.image;
              description = post.body;
              break;
            }
            case 'ContentfulPageBlogPost':
            default:
              url = `/blog/${post.slug}`;
              break;
          }
          return (
            <Card
              key={post.id}
              url={url}
              ctaLabel={ctaLabel}
              title={title}
              description={description}
              type={postType}
              image={image}
              readingTime={useContentType ? null : post.readTime}
            />
          );
        })}
      </div>
      {hasMore && (
        <div className={styles.loadMore} data-analytics-component="load-more">
          <ArrowLink label={'Load more'} onClick={onLoadMore} isArrowDown isLarge />
        </div>
      )}
    </>
  );
};

CardListing.propTypes = {
  posts: PropTypes.array.isRequired,
};

export default CardListing;
